import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import { EventClickArg, DatesSetArg } from '@fullcalendar/core';
import { DateClickArg } from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  ChevronLeft,
  ChevronRight,
  Today,
  ViewDay,
  ViewWeek,
  ViewModule,
  ViewList,
  Close,
  AccessTime,
  Edit,
  Visibility,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useCalendarEvents } from '../../hooks/useBookings';
import CreateBookingModal from '../../components/bookings/CreateBookingModal';
import { COLORS } from '@elevair/shared';
import { CalendarEvent } from '../../services/bookings';
import './calendar-styles.css';

type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek';

interface FullCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  extendedProps: {
    event: CalendarEvent;
  };
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const calendarRef = useRef<FullCalendar>(null);

  // State
  const [currentView, setCurrentView] = useState<CalendarView>('timeGridWeek');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: new Date(),
  });

  // Popover state
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<FullCalendarEvent | null>(null);

  // Create booking modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [preselectedDate, setPreselectedDate] = useState<Date | null>(null);

  // Fetch bookings for current date range
  const { data: calendarEvents, isLoading } = useCalendarEvents(
    dateRange.start.toISOString(),
    dateRange.end.toISOString()
  );

  // Transform calendar events to FullCalendar events
  const events: FullCalendarEvent[] =
    calendarEvents?.map((event) => ({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      backgroundColor: event.backgroundColor || COLORS.primary,
      borderColor: event.borderColor || COLORS.primary,
      textColor: '#FFFFFF',
      extendedProps: {
        event,
      },
    })) || [];

  // Handle date range change
  const handleDatesSet = useCallback((dateInfo: DatesSetArg) => {
    setCurrentDate(dateInfo.view.currentStart);
    setDateRange({
      start: dateInfo.start,
      end: dateInfo.end,
    });
  }, []);

  // Handle view change
  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: CalendarView | null
  ) => {
    if (newView && calendarRef.current) {
      setCurrentView(newView);
      calendarRef.current.getApi().changeView(newView);
    }
  };

  // Navigation handlers
  const handleToday = () => {
    calendarRef.current?.getApi().today();
  };

  const handlePrev = () => {
    calendarRef.current?.getApi().prev();
  };

  const handleNext = () => {
    calendarRef.current?.getApi().next();
  };

  // Handle event click (show popover)
  const handleEventClick = (info: EventClickArg) => {
    const eventProps = info.event.extendedProps as { event?: CalendarEvent };
    const fcEvent: FullCalendarEvent = {
      id: info.event.id,
      title: info.event.title,
      start: info.event.start?.toISOString() || '',
      end: info.event.end?.toISOString() || '',
      backgroundColor: info.event.backgroundColor,
      borderColor: info.event.borderColor,
      textColor: '#FFFFFF',
      extendedProps: { event: eventProps.event as CalendarEvent },
    };
    setSelectedEvent(fcEvent);
    setPopoverAnchor(info.el);
  };

  // Handle date/time click (create new booking)
  const handleDateClick = (info: DateClickArg) => {
    setPreselectedDate(info.date);
    setCreateModalOpen(true);
  };

  // Handle select (drag to create)
  const handleSelect = (info: { start: Date }) => {
    setPreselectedDate(info.start);
    setCreateModalOpen(true);
  };

  // Close popover
  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setSelectedEvent(null);
  };

  // Get title based on current view and date
  const getTitle = () => {
    if (currentView === 'dayGridMonth') {
      return format(currentDate, 'MMMM yyyy');
    } else if (currentView === 'timeGridWeek' || currentView === 'listWeek') {
      return format(currentDate, 'MMMM yyyy');
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    }
  };

  // Get status color
  const getStatusColor = (status?: string): string => {
    const colors: Record<string, string> = {
      scheduled: '#3B82F6',
      completed: '#10B981',
      cancelled: '#EF4444',
      no_show: '#F59E0B',
      rescheduled: '#8B5CF6',
    };
    return colors[status || ''] || '#6B7280';
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          Calendar
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setPreselectedDate(null);
            setCreateModalOpen(true);
          }}
        >
          New Booking
        </Button>
      </Box>

      {/* Calendar Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {/* Navigation */}
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleToday}
                startIcon={<Today />}
              >
                Today
              </Button>
              <IconButton onClick={handlePrev} size="small">
                <ChevronLeft />
              </IconButton>
              <IconButton onClick={handleNext} size="small">
                <ChevronRight />
              </IconButton>
              <Typography variant="h6" sx={{ ml: 1, minWidth: 200 }}>
                {getTitle()}
              </Typography>
            </Box>

            {/* View Toggle */}
            <ToggleButtonGroup
              value={currentView}
              exclusive
              onChange={handleViewChange}
              size="small"
            >
              <ToggleButton value="dayGridMonth" aria-label="month view">
                <ViewModule fontSize="small" />
              </ToggleButton>
              <ToggleButton value="timeGridWeek" aria-label="week view">
                <ViewWeek fontSize="small" />
              </ToggleButton>
              <ToggleButton value="timeGridDay" aria-label="day view">
                <ViewDay fontSize="small" />
              </ToggleButton>
              <ToggleButton value="listWeek" aria-label="list view">
                <ViewList fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card>
        <CardContent sx={{ position: 'relative', minHeight: 600 }}>
          {isLoading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="rgba(255,255,255,0.7)"
              zIndex={10}
            >
              <CircularProgress />
            </Box>
          )}

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView={currentView}
            headerToolbar={false}
            events={events}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            select={handleSelect}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={3}
            weekends={true}
            nowIndicator={true}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            slotDuration="00:30:00"
            allDaySlot={false}
            height="auto"
            expandRows={true}
            stickyHeaderDates={true}
            eventDisplay="block"
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
            }}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
            }}
          />
        </CardContent>
      </Card>

      {/* Event Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {selectedEvent && (
          <Box sx={{ p: 2, minWidth: 280, maxWidth: 320 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedEvent.title}
                </Typography>
                <Chip
                  size="small"
                  label={selectedEvent.extendedProps.event?.extendedProps?.status || 'scheduled'}
                  sx={{
                    mt: 0.5,
                    textTransform: 'capitalize',
                    bgcolor: getStatusColor(selectedEvent.extendedProps.event?.extendedProps?.status),
                    color: '#fff',
                  }}
                />
              </Box>
              <IconButton size="small" onClick={handleClosePopover}>
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {/* Time */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <AccessTime fontSize="small" color="action" />
              <Typography variant="body2">
                {format(new Date(selectedEvent.start), 'EEEE, MMMM d')}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={2} ml={3}>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(selectedEvent.start), 'h:mm a')} -{' '}
                {format(new Date(selectedEvent.end), 'h:mm a')}
              </Typography>
            </Box>

            {/* Client */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: '0.75rem',
                  bgcolor: COLORS.primary,
                }}
              >
                {selectedEvent.extendedProps.event?.extendedProps?.clientName
                  ?.split(' ')
                  .map((n: string) => n[0])
                  .join('')}
              </Avatar>
              <Typography variant="body2">
                {selectedEvent.extendedProps.event?.extendedProps?.clientName}
              </Typography>
            </Box>

            {/* Actions */}
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => {
                  navigate(`/bookings/${selectedEvent.id}`);
                  handleClosePopover();
                }}
                fullWidth
              >
                View
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<Edit />}
                onClick={() => {
                  navigate(`/bookings/${selectedEvent.id}/edit`);
                  handleClosePopover();
                }}
                fullWidth
              >
                Edit
              </Button>
            </Box>
          </Box>
        )}
      </Popover>

      {/* Create Booking Modal */}
      <CreateBookingModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setPreselectedDate(null);
        }}
        preselectedDate={preselectedDate}
      />
    </Box>
  );
}
